import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Product } from '@portal/portal-core';
import { Button as BaseButton } from 'components/styled-primitives';
import { isAssetsV2 } from 'utils/product-assets-utils';
import { validateUrl } from './validation-utils';

// The shared Button (FjButton) forwards unrecognized props to the underlying element at
// runtime; the legacy `small`/`primary` flags below are passed through unchanged. They are
// not part of Button's typed props, so widen the type locally to preserve exact runtime
// behavior while keeping the file strictly typed.
const Button = BaseButton as React.ComponentType<
  React.ComponentProps<typeof BaseButton> & { small?: boolean; primary?: boolean }
>;

interface ProductVersionForAssets {
  name: string;
  productVersion: string;
  // `assets` originates from loosely-typed redux state (unknown); narrowed via isAssetsV2 below.
  assets?: unknown;
}

interface ProductAssetsEditorProps {
  productVersion: ProductVersionForAssets;
  onSave: (assets: Product.AssetsV2) => void;
  onCancel: () => void;
}

interface ImportMessage {
  type: 'error' | 'success';
  text: string;
}

interface ImportValidationResult {
  valid: boolean;
  error?: string;
}

interface EditingItem {
  categoryId: string;
  itemIndex: number;
}

const isValidUrl = (string: string): boolean => {
  if (!string || !string.trim()) return true; // Empty is valid (optional field)
  const validation = validateUrl(string);
  return validation.valid;
};

const hasDuplicateCategoryId = (
  categories: Product.AssetCategory[],
  categoryId: string,
  excludeIndex = -1,
): boolean => {
  const lowerCategoryId = categoryId.toLowerCase();
  return categories.some(
    (cat, index) => index !== excludeIndex && cat.id.toLowerCase() === lowerCategoryId,
  );
};

const hasDuplicateItemLabel = (
  items: Product.AssetItem[],
  label: string,
  excludeIndex = -1,
): boolean => {
  if (!label || !label.trim()) return false; // Empty labels are allowed
  const lowerLabel = label.toLowerCase();
  return items.some(
    (item, index) =>
      index !== excludeIndex && !!item.label && item.label.toLowerCase() === lowerLabel,
  );
};

/**
 * Encodes special characters in URLs to ensure they pass URI validation
 * Handles characters like ™, &, (, ) by percent-encoding them
 * @param urlString - The URL string to encode
 * @returns The encoded URL string
 */
const encodeUrlForStorage = (urlString: string): string => {
  if (!urlString || !urlString.trim()) return urlString;

  try {
    // Parse the URL
    const url = new URL(urlString.trim());

    // Split pathname into segments and encode each one
    const pathSegments = url.pathname.split('/').map((segment) => {
      // Decode first in case it's already partially encoded
      try {
        const decoded = decodeURIComponent(segment);
        // Encode the segment and then explicitly encode ( and ) which encodeURIComponent doesn't encode
        return encodeURIComponent(decoded).replace(/\(/g, '%28').replace(/\)/g, '%29');
      } catch (e) {
        // If decode fails, just encode as-is
        return encodeURIComponent(segment).replace(/\(/g, '%28').replace(/\)/g, '%29');
      }
    });

    // Reconstruct the URL with encoded pathname
    url.pathname = pathSegments.join('/');

    return url.toString();
  } catch (e) {
    // If URL parsing fails, return as-is (might be relative or invalid)
    return urlString.trim();
  }
};

const ProductAssetsEditor = ({
  productVersion,
  onSave,
  onCancel,
}: ProductAssetsEditorProps): React.ReactElement => {
  const rawAssets = productVersion.assets as Product.ProductAssets | null | undefined;
  const initialAssets: Product.ProductAssets = rawAssets || {};

  // Convert any assets to v2 format for editing
  const initialCategories: Product.AssetCategory[] = isAssetsV2(initialAssets)
    ? initialAssets.categories || []
    : [];

  const [categories, setCategories] = useState<Product.AssetCategory[]>(initialCategories);
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingCategoryLabel, setEditingCategoryLabel] = useState<string | null>(null);
  const [editCategoryLabelValue, setEditCategoryLabelValue] = useState('');
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null); // { categoryId, itemIndex }
  const [editItemUrlValue, setEditItemUrlValue] = useState('');
  const [editItemLabelValue, setEditItemLabelValue] = useState('');
  const [newItemUrl, setNewItemUrl] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({}); // { categoryId: errorMessage }
  const [importMessage, setImportMessage] = useState<ImportMessage | null>(null); // { type: 'error' | 'success', text: string }
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleAddCategory = (): void => {
    if (newCategoryId.trim() && newCategoryLabel.trim()) {
      // Validate category ID format (alphanumeric, dashes, underscores)
      if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(newCategoryId.trim())) {
        setCategoryError(
          'Invalid category ID. Must start with a letter and contain only letters, numbers, dashes, and underscores.',
        );
        return;
      }

      // Check for duplicate category IDs (case-insensitive)
      if (hasDuplicateCategoryId(categories, newCategoryId.trim())) {
        setCategoryError(
          `Category ID "${newCategoryId.trim()}" already exists. Please use a unique ID.`,
        );
        return;
      }

      setCategories([
        ...categories,
        {
          id: newCategoryId.trim(),
          label: newCategoryLabel.trim(),
          items: [],
        },
      ]);
      setNewCategoryId('');
      setNewCategoryLabel('');
      setCategoryError('');
    }
  };

  const handleRemoveCategory = (categoryId: string): void => {
    setCategories(categories.filter((cat) => cat.id !== categoryId));
    if (editingCategory === categoryId) {
      setEditingCategory(null);
    }
  };

  const handleMoveCategoryUp = (index: number): void => {
    if (index > 0) {
      const newCategories = [...categories];
      [newCategories[index - 1], newCategories[index]] = [
        newCategories[index],
        newCategories[index - 1],
      ];
      setCategories(newCategories);
    }
  };

  const handleMoveCategoryDown = (index: number): void => {
    if (index < categories.length - 1) {
      const newCategories = [...categories];
      [newCategories[index], newCategories[index + 1]] = [
        newCategories[index + 1],
        newCategories[index],
      ];
      setCategories(newCategories);
    }
  };

  const handleStartEditCategoryLabel = (categoryId: string, currentLabel: string): void => {
    setEditingCategoryLabel(categoryId);
    setEditCategoryLabelValue(currentLabel);
  };

  const handleSaveCategoryLabel = (categoryId: string): void => {
    if (editCategoryLabelValue.trim()) {
      setCategories(
        categories.map((cat) => {
          if (cat.id === categoryId) {
            return { ...cat, label: editCategoryLabelValue.trim() };
          }
          return cat;
        }),
      );
      setEditingCategoryLabel(null);
      setEditCategoryLabelValue('');
    }
  };

  const handleCancelEditCategoryLabel = (): void => {
    setEditingCategoryLabel(null);
    setEditCategoryLabelValue('');
  };

  const handleAddItem = (categoryId: string): void => {
    if (!newItemUrl.trim()) {
      setItemErrors({ ...itemErrors, [categoryId]: 'Item URL is required.' });
      return;
    }

    if (!isValidUrl(newItemUrl)) {
      setItemErrors({
        ...itemErrors,
        [categoryId]: 'Invalid URL. Please enter a valid HTTP or HTTPS URL.',
      });
      return;
    }

    // Check for duplicate item label within the same category
    const category = categories.find((cat) => cat.id === categoryId);
    if (
      category &&
      newItemLabel.trim() &&
      hasDuplicateItemLabel(category.items, newItemLabel.trim())
    ) {
      setItemErrors({
        ...itemErrors,
        [categoryId]: `An item with the label "${newItemLabel.trim()}" already exists in this category. Please use a unique label.`,
      });
      return;
    }

    setCategories(
      categories.map((cat) => {
        if (cat.id === categoryId) {
          const newItem: Product.AssetItem = { url: encodeUrlForStorage(newItemUrl.trim()) };
          if (newItemLabel.trim()) {
            newItem.label = newItemLabel.trim();
          }
          return {
            ...cat,
            items: [...cat.items, newItem],
          };
        }
        return cat;
      }),
    );

    setNewItemUrl('');
    setNewItemLabel('');
    // Clear error for this category
    const newErrors = { ...itemErrors };
    delete newErrors[categoryId];
    setItemErrors(newErrors);
  };

  const handleRemoveItem = (categoryId: string, itemIndex: number): void => {
    setCategories(
      categories.map((cat) => {
        if (cat.id === categoryId) {
          return {
            ...cat,
            items: cat.items.filter((_, i) => i !== itemIndex),
          };
        }
        return cat;
      }),
    );
  };

  const handleMoveItemUp = (categoryId: string, itemIndex: number): void => {
    if (itemIndex > 0) {
      setCategories(
        categories.map((cat) => {
          if (cat.id === categoryId) {
            const newItems = [...cat.items];
            [newItems[itemIndex - 1], newItems[itemIndex]] = [
              newItems[itemIndex],
              newItems[itemIndex - 1],
            ];
            return { ...cat, items: newItems };
          }
          return cat;
        }),
      );
    }
  };

  const handleMoveItemDown = (categoryId: string, itemIndex: number): void => {
    setCategories(
      categories.map((cat) => {
        if (cat.id === categoryId) {
          if (itemIndex < cat.items.length - 1) {
            const newItems = [...cat.items];
            [newItems[itemIndex], newItems[itemIndex + 1]] = [
              newItems[itemIndex + 1],
              newItems[itemIndex],
            ];
            return { ...cat, items: newItems };
          }
        }
        return cat;
      }),
    );
  };

  const handleStartEditItem = (
    categoryId: string,
    itemIndex: number,
    currentUrl: string,
    currentLabel?: string,
  ): void => {
    setEditingItem({ categoryId, itemIndex });
    setEditItemUrlValue(currentUrl);
    setEditItemLabelValue(currentLabel || '');
  };

  const handleSaveItem = (): void => {
    if (!editItemUrlValue.trim()) {
      setItemErrors({ ...itemErrors, [editingItem!.categoryId]: 'Item URL is required.' });
      return;
    }

    // Encode URL before validation
    const encodedUrl = encodeUrlForStorage(editItemUrlValue.trim());

    if (!isValidUrl(encodedUrl)) {
      setItemErrors({
        ...itemErrors,
        [editingItem!.categoryId]: 'Invalid URL. Please enter a valid HTTP or HTTPS URL.',
      });
      return;
    }

    // Check for duplicate item label within the same category (excluding current item)
    const category = categories.find((cat) => cat.id === editingItem!.categoryId);
    if (
      category &&
      editItemLabelValue.trim() &&
      hasDuplicateItemLabel(category.items, editItemLabelValue.trim(), editingItem!.itemIndex)
    ) {
      setItemErrors({
        ...itemErrors,
        [editingItem!.categoryId]: `An item with the label "${editItemLabelValue.trim()}" already exists in this category. Please use a unique label.`,
      });
      return;
    }

    setCategories(
      categories.map((cat) => {
        if (cat.id === editingItem!.categoryId) {
          const newItems = [...cat.items];
          const updatedItem: Product.AssetItem = { url: encodedUrl };
          if (editItemLabelValue.trim()) {
            updatedItem.label = editItemLabelValue.trim();
          }
          newItems[editingItem!.itemIndex] = updatedItem;
          return { ...cat, items: newItems };
        }
        return cat;
      }),
    );

    setEditingItem(null);
    setEditItemUrlValue('');
    setEditItemLabelValue('');
    // Clear error for this category
    const newErrors = { ...itemErrors };
    delete newErrors[editingItem!.categoryId];
    setItemErrors(newErrors);
  };

  const handleCancelEditItem = (): void => {
    setEditingItem(null);
    setEditItemUrlValue('');
    setEditItemLabelValue('');
  };

  const handleExportAssets = (): void => {
    const assets: Product.AssetsV2 = {
      version: 2,
      categories,
    };

    const jsonString = JSON.stringify(assets, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${productVersion.name}-v${productVersion.productVersion}-assets.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setImportMessage({
      type: 'success',
      text: 'Assets exported successfully',
    });
    setTimeout(() => setImportMessage(null), 3000);
  };

  // `assets` comes from JSON.parse of an imported file, so it's an untrusted boundary value.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validateImportedAssets = (assets: any): ImportValidationResult => {
    // Check that it's an object
    if (!assets || typeof assets !== 'object') {
      return { valid: false, error: 'Invalid JSON: must be an object' };
    }

    // Check version
    if (assets.version !== 2) {
      return {
        valid: false,
        error: 'Invalid assets format: must be version 2',
      };
    }

    // Check categories is an array
    if (!Array.isArray(assets.categories)) {
      return {
        valid: false,
        error: 'Invalid assets format: categories must be an array',
      };
    }

    // Validate each category
    const categoryIds = new Set<string>();
    for (let i = 0; i < assets.categories.length; i += 1) {
      const category = assets.categories[i];

      // Check category has required fields
      if (!category.id || !category.label) {
        return {
          valid: false,
          error: `Category at index ${i} is missing required fields (id, label)`,
        };
      }

      // Validate category ID format
      if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(category.id)) {
        return {
          valid: false,
          error: `Category "${category.id}" has invalid ID format. Must start with a letter and contain only letters, numbers, dashes, and underscores.`,
        };
      }

      // Check for duplicate category IDs (case-insensitive)
      const lowerCategoryId = category.id.toLowerCase();
      if (categoryIds.has(lowerCategoryId)) {
        return {
          valid: false,
          error: `Duplicate category ID: "${category.id}"`,
        };
      }
      categoryIds.add(lowerCategoryId);

      // Check items is an array
      if (!Array.isArray(category.items)) {
        return {
          valid: false,
          error: `Category "${category.label}" must have an items array`,
        };
      }

      // Validate each item in the category
      const itemLabels = new Set<string>();
      for (let j = 0; j < category.items.length; j += 1) {
        const item = category.items[j];

        // Check item has required url field
        if (!item.url) {
          return {
            valid: false,
            error: `Item at index ${j} in category "${category.label}" is missing required url field`,
          };
        }

        // Validate URL
        if (!isValidUrl(item.url)) {
          return {
            valid: false,
            error: `Item at index ${j} in category "${category.label}" has invalid URL: ${item.url}`,
          };
        }

        // Check for duplicate labels (case-insensitive) if label exists
        if (item.label && item.label.trim()) {
          const lowerLabel = item.label.toLowerCase();
          if (itemLabels.has(lowerLabel)) {
            return {
              valid: false,
              error: `Duplicate item label in category "${category.label}": "${item.label}"`,
            };
          }
          itemLabels.add(lowerLabel);
        }
      }
    }

    return { valid: true };
  };

  const handleImportAssets = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Reset file input to allow re-importing the same file
    const { target } = event;
    target.value = null as unknown as string;
    setImportMessage(null);

    try {
      const text = await file.text();
      const assets = JSON.parse(text);

      const validation = validateImportedAssets(assets);
      if (!validation.valid) {
        setImportMessage({ type: 'error', text: validation.error! });
        return;
      }

      // Import successful - update categories
      setCategories(assets.categories);
      setImportMessage({
        type: 'success',
        text: `Successfully imported ${assets.categories.length} categor${
          assets.categories.length === 1 ? 'y' : 'ies'
        }`,
      });
      setTimeout(() => setImportMessage(null), 5000);
    } catch (error) {
      if (error instanceof SyntaxError) {
        setImportMessage({ type: 'error', text: 'Invalid JSON file' });
      } else {
        setImportMessage({
          type: 'error',
          text: `Failed to read file: ${(error as Error).message}`,
        });
      }
    }
  };

  const handleSave = (): void => {
    // Validate all URLs in all categories
    const hasInvalidUrl = categories.some((category) =>
      category.items.some((item) => {
        if (!isValidUrl(item.url)) {
          console.error(`Invalid URL in category "${category.label}": ${item.url}`);
          return true;
        }
        return false;
      }),
    );

    if (hasInvalidUrl) {
      return;
    }

    const assets: Product.AssetsV2 = {
      version: 2,
      categories,
    };

    onSave(assets);
  };

  return (
    <ModalBackdrop onClick={handleBackdropClick}>
      <EditorContainer>
        <Header>
          <Title data-test="editor-title">Edit Product Assets</Title>
          <Subtitle data-test="editor-subtitle">
            Manage downloadable assets for {productVersion.name} v{productVersion.productVersion}
          </Subtitle>
          <Description>
            Organize assets into flexible categories. Each category can contain multiple
            downloadable items.
          </Description>
          <Description>
            <strong>Important:</strong> For Infinicyt products, a category with ID
            &quot;installer&quot; (case-insensitive) is used in backend email templates. The first
            item URL in this category becomes the download link (urls.download) in invitation and
            welcome emails.
          </Description>
        </Header>

        {/* Add New Category Section */}
        <Section>
          <SectionTitle>Add New Category</SectionTitle>
          <AddCategorySection>
            <InputGroup>
              <Label>Category ID</Label>
              <Input
                type="text"
                placeholder="e.g., installer, reference-databases, documentation"
                value={newCategoryId}
                onChange={(e) => {
                  setNewCategoryId(e.target.value);
                  setCategoryError('');
                }}
                data-test="new-category-id-input"
              />
              <HelpText>
                Must start with a letter and contain only letters, numbers, dashes, and underscores
              </HelpText>
            </InputGroup>
            {categoryError && (
              <ErrorMessage data-test="category-error">{categoryError}</ErrorMessage>
            )}
            <InputGroup>
              <Label>Category Label</Label>
              <Input
                type="text"
                placeholder="e.g., Installer, Reference Databases, Documentation"
                value={newCategoryLabel}
                onChange={(e) => setNewCategoryLabel(e.target.value)}
                data-test="new-category-label-input"
              />
            </InputGroup>
            <Button
              onClick={handleAddCategory}
              disabled={!newCategoryId.trim() || !newCategoryLabel.trim()}
              data-test="add-category-button"
            >
              Add Category
            </Button>
          </AddCategorySection>
        </Section>

        {/* Existing Categories */}
        {categories.map((category, categoryIndex) => (
          <Section key={category.id}>
            <CategoryHeader>
              {editingCategoryLabel === category.id ? (
                <EditLabelContainer>
                  <Input
                    type="text"
                    value={editCategoryLabelValue}
                    onChange={(e) => setEditCategoryLabelValue(e.target.value)}
                    data-test={`edit-category-label-input-${category.id}`}
                    placeholder="Category label"
                    autoFocus
                  />
                  <ButtonGroup>
                    <Button
                      onClick={() => handleSaveCategoryLabel(category.id)}
                      disabled={!editCategoryLabelValue.trim()}
                      data-test={`save-category-label-${category.id}`}
                      small
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancelEditCategoryLabel}
                      data-test={`cancel-edit-category-label-${category.id}`}
                      small
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </EditLabelContainer>
              ) : (
                <>
                  <CategoryTitleRow>
                    <SectionTitle>{category.label}</SectionTitle>
                    <EditButton
                      onClick={() => handleStartEditCategoryLabel(category.id, category.label)}
                      data-test={`edit-category-label-${category.id}`}
                      title="Edit category label"
                    >
                      ✏️
                    </EditButton>
                  </CategoryTitleRow>
                  <ButtonGroup>
                    <ReorderButton
                      onClick={() => handleMoveCategoryUp(categoryIndex)}
                      disabled={categoryIndex === 0}
                      data-test={`move-category-up-${category.id}`}
                      title="Move category up"
                    >
                      ▲
                    </ReorderButton>
                    <ReorderButton
                      onClick={() => handleMoveCategoryDown(categoryIndex)}
                      disabled={categoryIndex === categories.length - 1}
                      data-test={`move-category-down-${category.id}`}
                      title="Move category down"
                    >
                      ▼
                    </ReorderButton>
                    <RemoveButton
                      onClick={() => handleRemoveCategory(category.id)}
                      data-test={`remove-category-${category.id}`}
                    >
                      Remove Category
                    </RemoveButton>
                  </ButtonGroup>
                </>
              )}
            </CategoryHeader>

            {/* Existing Items in Category */}
            {category.items.length > 0 && (
              <ItemsList>
                {category.items.map((item, index) => (
                  <ItemRow key={`${category.id}-${item.url}`} data-test="asset-item">
                    {editingItem?.categoryId === category.id && editingItem?.itemIndex === index ? (
                      <>
                        <ItemEditContainer>
                          <InputGroup>
                            <Label>Item URL</Label>
                            <Input
                              type="text"
                              value={editItemUrlValue}
                              onChange={(e) => setEditItemUrlValue(e.target.value)}
                              data-test={`edit-item-url-${category.id}-${index}`}
                              placeholder="https://example.com/file.zip"
                            />
                          </InputGroup>
                          <InputGroup>
                            <Label>Item Label (Optional)</Label>
                            <Input
                              type="text"
                              value={editItemLabelValue}
                              onChange={(e) => setEditItemLabelValue(e.target.value)}
                              data-test={`edit-item-label-${category.id}-${index}`}
                              placeholder="e.g., Software v3.0.0"
                            />
                          </InputGroup>
                          <ButtonGroup>
                            <Button
                              onClick={handleSaveItem}
                              disabled={!editItemUrlValue.trim()}
                              data-test={`save-item-${category.id}-${index}`}
                              small
                            >
                              Save
                            </Button>
                            <Button
                              onClick={handleCancelEditItem}
                              data-test={`cancel-edit-item-${category.id}-${index}`}
                              small
                            >
                              Cancel
                            </Button>
                          </ButtonGroup>
                        </ItemEditContainer>
                      </>
                    ) : (
                      <>
                        <ItemInfo>
                          {item.label && <ItemLabel>{item.label}</ItemLabel>}
                          <ItemUrl>{item.url}</ItemUrl>
                        </ItemInfo>
                        <ButtonGroup>
                          <EditButton
                            onClick={() =>
                              handleStartEditItem(category.id, index, item.url, item.label)
                            }
                            data-test={`edit-item-${category.id}-${index}`}
                            title="Edit item"
                          >
                            ✏️
                          </EditButton>
                          <ReorderButton
                            onClick={() => handleMoveItemUp(category.id, index)}
                            disabled={index === 0}
                            data-test={`move-item-up-${category.id}-${index}`}
                            title="Move item up"
                          >
                            ▲
                          </ReorderButton>
                          <ReorderButton
                            onClick={() => handleMoveItemDown(category.id, index)}
                            disabled={index === category.items.length - 1}
                            data-test={`move-item-down-${category.id}-${index}`}
                            title="Move item down"
                          >
                            ▼
                          </ReorderButton>
                          <RemoveButton
                            onClick={() => handleRemoveItem(category.id, index)}
                            data-test={`remove-item-${category.id}-${index}`}
                          >
                            Remove
                          </RemoveButton>
                        </ButtonGroup>
                      </>
                    )}
                  </ItemRow>
                ))}
              </ItemsList>
            )}

            {/* Add Item to Category */}
            {editingCategory === category.id && (
              <AddItemSection>
                <InputGroup>
                  <Label>Item URL</Label>
                  <Input
                    type="text"
                    placeholder="https://example.com/file.zip"
                    value={newItemUrl}
                    onChange={(e) => {
                      setNewItemUrl(e.target.value);
                      const newErrors = { ...itemErrors };
                      delete newErrors[category.id];
                      setItemErrors(newErrors);
                    }}
                    data-test={`new-item-url-${category.id}`}
                  />
                </InputGroup>
                <InputGroup>
                  <Label>Item Label (Optional)</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Software v3.0.0"
                    value={newItemLabel}
                    onChange={(e) => setNewItemLabel(e.target.value)}
                    data-test={`new-item-label-${category.id}`}
                  />
                </InputGroup>
                {itemErrors[category.id] && (
                  <ErrorMessage data-test={`item-error-${category.id}`}>
                    {itemErrors[category.id]}
                  </ErrorMessage>
                )}
                <ButtonRow>
                  <Button
                    onClick={() => handleAddItem(category.id)}
                    disabled={!newItemUrl.trim()}
                    data-test={`add-item-${category.id}`}
                  >
                    Add Item
                  </Button>
                  <Button onClick={() => setEditingCategory(null)} data-test="cancel-add-item">
                    Cancel
                  </Button>
                </ButtonRow>
              </AddItemSection>
            )}

            {!editingCategory && (
              <Button
                onClick={() => setEditingCategory(category.id)}
                data-test={`edit-category-${category.id}`}
              >
                Add Item to {category.label}
              </Button>
            )}
          </Section>
        ))}

        {importMessage && (
          <MessageContainer>
            {importMessage.type === 'error' ? (
              <ImportErrorMessage data-test="import-error">{importMessage.text}</ImportErrorMessage>
            ) : (
              <ImportSuccessMessage data-test="import-success">
                {importMessage.text}
              </ImportSuccessMessage>
            )}
          </MessageContainer>
        )}

        <ButtonContainer>
          <LeftButtonGroup>
            <Button onClick={handleExportAssets} data-test="export-assets-button">
              Export Assets
            </Button>
            <Button onClick={() => fileInputRef.current?.click()} data-test="import-assets-button">
              Import Assets
            </Button>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportAssets}
              data-test="import-file-input"
            />
          </LeftButtonGroup>
          <RightButtonGroup>
            <Button onClick={onCancel} data-test="cancel-button">
              Cancel
            </Button>
            <Button onClick={handleSave} primary data-test="save-button">
              Save Assets
            </Button>
          </RightButtonGroup>
        </ButtonContainer>
      </EditorContainer>
    </ModalBackdrop>
  );
};

export default ProductAssetsEditor;

// Styled Components
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the table-toolbar dropdown (1002) and the FjTooltip visor (.fj-tooltip-portal .visor,
     1001) - both share this component's stacking context via document.body portals, and a modal
     must render above either if one is left open/lingering underneath it. */
  z-index: 1003;
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  min-width: 800px;
  width: 90vw;
  max-width: 1200px;
  max-height: 80vh;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.productTitle};
`;

const Description = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.productTitle};
  font-style: italic;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.tabBackground};
  border-radius: 8px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const CategoryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const EditButton = styled.button`
  padding: 4px 8px;
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.black};
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0.6;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const AddCategorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddItemSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 2px dashed ${({ theme }) => theme.colors.notSelectable};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
  }

  &::placeholder {
    color: #bdbdbd;
    opacity: 0.7;
    font-style: italic;
  }
`;

const HelpText = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.productTitle};
  font-style: italic;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  gap: 12px;
`;

const ItemEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const ItemLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
`;

const ItemUrl = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.productTitle};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ReorderButton = styled.button`
  padding: 4px 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.black};
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.selectableLightBg};
    border-color: ${({ theme }) => theme.colors.black};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid #d32f2f;
  color: #d32f2f;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #d32f2f;
    color: ${({ theme }) => theme.colors.white};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.notSelectable};
`;

const LeftButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const RightButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const MessageContainer = styled.div`
  margin-top: 8px;
`;

const ImportErrorMessage = styled.div`
  padding: 12px;
  background-color: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 4px;
  color: #c62828;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⚠️';
    font-size: 16px;
  }
`;

const ImportSuccessMessage = styled.div`
  padding: 12px;
  background-color: #e8f5e9;
  border: 1px solid #66bb6a;
  border-radius: 4px;
  color: #2e7d32;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '✓';
    font-size: 16px;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background-color: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 4px;
  color: #c62828;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⚠️';
    font-size: 16px;
  }
`;
